import React, { FC, useState, useEffect } from "react";
import {
  Container,
  HeadWrapper,
  BodyModal,
  Icon,
} from '../create-subnet/styles'
import { icons } from "../../../icons";
import Button from "../../button";
import { useTranslation } from "react-i18next";
import * as yup from 'yup';
import { useFormik } from 'formik';
import Input from "../../input";
import Select from "../../select";
import CheckBox from "../../checkbox";
import { HeadLine3 } from "../../typography/styled";
import { BodyDefault } from "../../typography/styled";
import { billingService } from "../../../api";

type ModalProps = {
  closeModalCancel: () => void;
  closeModal: (props?: any) => void;
  dataToEdit?: any;
  loading?: boolean;
  alertTitle?: string;
}

const ProductCycleEdit: FC<ModalProps> = (props) => {
  const {
    closeModal,
    closeModalCancel,
    dataToEdit,
  } = props
  const [t] = useTranslation()
  const [submitting, setSubmitting] = useState(false)
  const [createOptions, setCreateOptions] = useState<any>(null)
  const [optionsLoading, setOptionsLoading] = useState(true)
  const [defaultCurrency, setDefaultCurrency] = useState<any>(null)
  const [backendErrors, setBackendErrors] = useState<any>({})

  const product = dataToEdit?.product;
  const productCycle = dataToEdit?.productCycle;
  const isAddMode = !productCycle;

  useEffect(() => {
    billingService.getProductCycleCreateOptions()
      .then((res: any) => {
        const options = res.data.create_options || res.data;
        setCreateOptions(options);
        const defCurrency = options.currencies?.find((c: any) => c.is_default);
        setDefaultCurrency(defCurrency);
        setOptionsLoading(false);
      })
      .catch(() => {
        setOptionsLoading(false);
        closeModalCancel();
      });
  }, []);

  const validationSchema = yup.object().shape({
    cycle: yup.string().required(`${t("REQUIRED_FIELD")}`),
    cycle_multiplier: yup.number().when('cycle', {
      is: (val: string) => val !== 'onetime',
      then: (schema) => schema.min(1).required(`${t("REQUIRED_FIELD")}`),
      otherwise: (schema) => schema.nullable(),
    }),
    currency: yup.string().required(`${t("REQUIRED_FIELD")}`),
    fixed_price: yup.number().when('is_relative_price', {
      is: false,
      then: (schema) => schema.min(0).required(`${t("REQUIRED_FIELD")}`),
      otherwise: (schema) => schema.nullable(),
    }),
    setup_fee: yup.number().when(['is_relative_price', 'cycle'], {
      is: (isRelative: boolean, cycle: string) => !isRelative && cycle !== 'onetime',
      then: (schema) => schema.min(0).required(`${t("REQUIRED_FIELD")}`),
      otherwise: (schema) => schema.nullable(),
    }),
    status: yup.string().required(`${t("REQUIRED_FIELD")}`),
  });

  const getInitialValues = () => {
    if (productCycle) {
      return {
        product: product?.id,
        cycle: productCycle.cycle || '',
        cycle_multiplier: productCycle.cycle_multiplier || 1,
        currency: productCycle.currency || '',
        is_relative_price: productCycle.is_relative_price || false,
        fixed_price: productCycle.fixed_price || 0,
        setup_fee: productCycle.setup_fee || 0,
        status: productCycle.status || '',
      };
    }
    return {
      product: product?.id,
      cycle: '',
      cycle_multiplier: 1,
      currency: '',
      is_relative_price: false,
      fixed_price: 0,
      setup_fee: 0,
      status: '',
    };
  };

  const onSubmit = async (values: any) => {
    setSubmitting(true);
    setBackendErrors({});

    const payload = { ...values };

    // If currency is default, force is_relative_price to false
    if (defaultCurrency && values.currency === defaultCurrency.code) {
      payload.is_relative_price = false;
    }

    // If onetime, don't send cycle_multiplier and setup_fee
    if (values.cycle === 'onetime') {
      delete payload.cycle_multiplier;
      delete payload.setup_fee;
    }

    try {
      if (isAddMode) {
        await billingService.createProductCycle(payload);
      } else {
        await billingService.updateProductCycle(productCycle.id, payload);
      }
      closeModal(true);
    } catch (e: any) {
      setBackendErrors(e?.response?.data || {});
      setSubmitting(false);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema,
    initialValues: getInitialValues(),
    onSubmit,
  });

  const { values, errors, touched, handleSubmit, handleChange, handleBlur, setFieldValue } = formik;

  // Set defaults once options are loaded
  useEffect(() => {
    if (!createOptions || !defaultCurrency) return;
    if (isAddMode) {
      if (createOptions.cycles?.length > 0 && !values.cycle) {
        setFieldValue('cycle', createOptions.cycles[0][0]);
      }
      if (createOptions.statuses?.length > 0 && !values.status) {
        setFieldValue('status', createOptions.statuses[0][0]);
      }
      if (defaultCurrency && !values.currency) {
        setFieldValue('currency', defaultCurrency.code);
      }
    }
  }, [createOptions, defaultCurrency]);

  const isDefaultCurrency = defaultCurrency && values.currency === defaultCurrency.code;
  const isOnetime = values.cycle === 'onetime';
  const isRelative = values.is_relative_price;

  const getPriceLabel = () => {
    if (isOnetime) return t("PRICE");
    if (product?.price_model === 'fixed_and_dynamic') return t("FIXED_PRICE");
    if (product?.price_model === 'dynamic_or_fixed') return t("MINIMUM_PRICE");
    return t("FIXED_PRICE");
  };

  const cycleOptions = createOptions?.cycles?.map((c: any) => ({
    value: c[0],
    label: c[1],
  })) || [];

  const currencyOptions = createOptions?.currencies?.map((c: any) => ({
    value: c.code,
    label: c.code,
  })) || [];

  const statusOptions = createOptions?.statuses?.map((s: any) => ({
    value: s[0],
    label: s[1],
  })) || [];

  if (optionsLoading) {
    return (
      <Container>
        <HeadWrapper>
          <HeadLine3 style={{ marginLeft: '10px', marginTop: '10px' }}>
            {t("LOADING")}...
          </HeadLine3>
          <Icon onClick={closeModalCancel} src={icons.CloseIcon} />
        </HeadWrapper>
      </Container>
    );
  }

  const formikProps = {
    errors: { ...errors, ...backendErrors },
    values,
    touched,
    handleChange,
    handleBlur,
  };

  return (
    <Container style={{ width: '460px' }}>
      <HeadWrapper>
        <HeadLine3 style={{ marginLeft: '10px', marginTop: '10px' }}>
          {isAddMode
            ? `${t("ADD_CYCLE")} — ${product?.name || ''}`
            : t("EDIT_CYCLE")
          }
        </HeadLine3>
        <Icon onClick={closeModalCancel} src={icons.CloseIcon} />
      </HeadWrapper>
      <BodyModal>
        <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
          <div style={{ flex: 1 }}>
            <Select
              required
              title={t("CYCLE")}
              data={cycleOptions}
              selectedValue={values.cycle}
              onChange={(val: any) => setFieldValue('cycle', val)}
              fieldName="cycle"
              errors={formikProps.errors}
              touched={touched}
              customStyles={{ width: '100%' }}
            />
          </div>
          {!isOnetime && (
            <div style={{ flex: 1 }}>
              <Input
                required
                title={t("CYCLE_MULTIPLIER")}
                fieldName='cycle_multiplier'
                placeholder=''
                onBlur={handleBlur}
                customStyles={{ width: '100%' }}
                type='number'
                {...formikProps}
              />
            </div>
          )}
        </div>

        <Select
          required
          title={t("CURRENCY")}
          data={currencyOptions}
          selectedValue={values.currency}
          onChange={(val: any) => setFieldValue('currency', val)}
          fieldName="currency"
          errors={formikProps.errors}
          touched={touched}
          customStyles={{ width: '100%' }}
        />

        <CheckBox
          title={t("AUTO_CALCULATE_PRICES")}
          value={isDefaultCurrency ? false : values.is_relative_price}
          onChange={() => {
            if (!isDefaultCurrency) {
              setFieldValue('is_relative_price', !values.is_relative_price);
            }
          }}
          disabled={isDefaultCurrency}
        />

        {isRelative && !isDefaultCurrency ? (
          <BodyDefault style={{ opacity: 0.7, fontSize: '13px' }}>
            {t("PRICES_CALCULATED_AUTOMATICALLY")}
          </BodyDefault>
        ) : (
          <div style={{ width:'100%' }} >
            <Input
              required
              title={getPriceLabel()}
              fieldName='fixed_price'
              placeholder='0.00'
              onBlur={handleBlur}
              customStyles={{ width: '100%' }}
              type='number'
              {...formikProps}
            />
            {!isOnetime && (
              <Input
                required
                title={t("SETUP_FEE")}
                fieldName='setup_fee'
                placeholder='0.00'
                onBlur={handleBlur}
                customStyles={{ width: '100%' }}
                type='number'
                {...formikProps}
              />
            )}
          </div>
        )}

        <Select
          required
          title={t("STATUS")}
          data={statusOptions}
          selectedValue={values.status}
          onChange={(val: any) => setFieldValue('status', val)}
          fieldName="status"
          errors={formikProps.errors}
          touched={touched}
          customStyles={{ width: '100%' }}
        />

        {backendErrors?.detail && (
          <BodyDefault style={{ color: 'red', fontSize: '13px' }}>
            {backendErrors.detail}
          </BodyDefault>
        )}
        {backendErrors?.non_field_errors && (
          <BodyDefault style={{ color: 'red', fontSize: '13px' }}>
            {Array.isArray(backendErrors.non_field_errors)
              ? backendErrors.non_field_errors.join(', ')
              : backendErrors.non_field_errors}
          </BodyDefault>
        )}

        <div style={{ display: "flex", gap: '6px', marginTop: '14px', width: '100%' }}>
          <Button
            variant="stroke"
            size="display"
            customStyles={{ flex: 1, justifyContent: 'center' }}
            onClick={closeModalCancel}
            title={t("CANCEL")}
          />
          <Button
            variant="primary"
            size="display"
            customStyles={{ flex: 1, justifyContent: 'center' }}
            onClick={handleSubmit}
            loading={submitting}
            title={isAddMode ? t("CREATE") : t("SAVE")}
          />
        </div>
      </BodyModal>
    </Container>
  )
}

export default ProductCycleEdit
